import React, { useCallback, useEffect, useState } from "react";

import { Stack } from "@mui/material";

import useCheckPermissions from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermission";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { typedKeys } from "@src/multiCustodian/utils/arrayHelpers";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import Modal from "@src/sharedComponents/Modal/Modal";
import FileDropUploadComponent from "@src/sharedComponents/SingleFileDropUpload/SingleFileDropUpload";
import Text from "@src/sharedComponents/Typography/Text";

import BulkUpdate from "./BulkUpdateText";
import CreateCustomModel from "./CreateCustomModel";
import CreateNewPartner from "./CreateNewPartner";
import CSVDownloadButton from "./CSVDownloadButton";
import ValidationErrors from "./ValidationErrors";

type Props = {
  clientCSV: File | null;
  setClientCSV: (file: File | null) => void;
  isOpen: boolean;
  onClose: () => void;
  isMutating: boolean;
  onSubmit: () => void;
  validationErrors: string[] | null;
};

const CSVModalUpload: React.FC<Props> = ({
  clientCSV,
  setClientCSV,
  isOpen,
  onClose,
  isMutating,
  onSubmit,
  validationErrors,
}): JSX.Element => {
  const {
    color: { $border },
  } = useTheme();

  const auth = useAdvisorRequestAuth();

  const canUseFamilyPath = useCheckPermissions(
    { type: "PortfolioPlan", id: null, action: "create:publicOrganization" },
    auth
  );

  useEffect(() => {
    if (canUseFamilyPath?.data?.authorized) {
      setSelectedTab("bulkUpdate");
    }
  }, [canUseFamilyPath]);

  const [selectedTab, setSelectedTab] = useState<TabKey>(
    canUseFamilyPath?.data?.authorized ? "bulkUpdate" : "createCustom"
  );

  const onCancel = useCallback(() => {
    setClientCSV(null);
    onClose();
  }, [onClose, setClientCSV]);

  return (
    <Modal isOpen={isOpen} closeModal={onClose} modalStyle={{ width: "620px" }}>
      <Stack direction={"row"} spacing={2}>
        <Text variant={"title2"} style={{ alignContent: "center" }}>
          Upload a spreadsheet
        </Text>
        <Stack
          style={{
            border: `1px solid ${$border}`,
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          <Text variant={"bodySmall"}>Model Portfolios</Text>
        </Stack>
      </Stack>
      <Spacer verticalSpacing="24px" />
      <FileDropUploadComponent
        onChange={setClientCSV}
        file={clientCSV}
        acceptedFileExtensions={[".csv"]}
        text={"Drop your file here or "}
        pressText={<Text style={{ textDecoration: "underline" }}>browse</Text>}
      />
      <Stack direction={"row"} justifyContent={"space-between"}>
        <CSVDownloadButton />
        <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
          <Button
            disabled={isMutating}
            text={"Cancel"}
            variant="text"
            onClick={onCancel}
            buttonType="primary"
          />

          <Button
            disabled={isMutating || clientCSV === null}
            text="Continue"
            variant="contained"
            buttonType="primary"
            onClick={onSubmit}
          />
        </Stack>
      </Stack>
      <Spacer verticalSpacing="24px" />

      {validationErrors !== null ? (
        <ValidationErrors validationErrors={validationErrors} />
      ) : (
        <>
          <Text variant="bodyBold">How to prepare the spreadsheet</Text>

          <Spacer verticalSpacing="12px" />

          {canUseFamilyPath?.data?.authorized === false ? (
            <>
              <ModelTab
                curTab={selectedTab}
                setCurTab={setSelectedTab}
                isAdmin={false}
              />
              <CreateCustomModel isAdmin={false} />
            </>
          ) : (
            <>
              <ModelTab
                curTab={selectedTab}
                setCurTab={setSelectedTab}
                isAdmin={true}
              />
              {selectedTab === "bulkUpdate" ? (
                <BulkUpdate />
              ) : selectedTab === "newPartner" ? (
                <CreateNewPartner />
              ) : (
                <CreateCustomModel isAdmin={true} />
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CSVModalUpload;

const TABS = {
  bulkUpdate: {
    title: "Bulk update existing models",
    visibileToNonAdmin: false,
  },
  newPartner: { title: "Create a new partner", visibileToNonAdmin: false },
  createCustom: { title: "Create a custom model", visibileToNonAdmin: true },
} as const;

type TabKey = keyof typeof TABS;

type TabsProps = {
  setCurTab: (tab: TabKey) => void;
  curTab: TabKey;
  isAdmin?: boolean;
};

const ModelTab = ({ curTab, setCurTab, isAdmin }: TabsProps): JSX.Element => {
  const {
    color: { $borderBold, $borderSelected },
  } = useTheme();

  React.useEffect(() => {
    if (!isAdmin && curTab !== "createCustom") {
      setCurTab("createCustom");
    }
  }, [isAdmin, curTab, setCurTab]);

  return (
    <Stack
      direction={"row"}
      spacing={2}
      style={{ borderBottom: `1px solid ${$borderBold}`, marginBottom: "12px" }}
    >
      {typedKeys(TABS)
        .filter((tab) => isAdmin || TABS[tab].visibileToNonAdmin)
        .map((tab) => (
          <Text
            variant="bodySmall"
            key={tab}
            style={{
              margin: "0px 24px 0px 0px",
              padding: "0px 0px 8px 0px",
              cursor: "pointer",
              borderBottom: `2px solid ${
                curTab === tab ? $borderSelected : "transparent"
              }`,
            }}
            onClick={() => setCurTab(tab)}
          >
            {TABS[tab].title}
          </Text>
        ))}
    </Stack>
  );
};
